// src/components/BookedFlatInvoice/BookedFlatInvoice.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import dayjs from 'dayjs';
import api from '../../api.js';
import Dashheader from '../Dashboard/Dashheader/Dashheader';

// Helper function to convert numbers to words (Indian system)
const units = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function convertNumberToWordsIndian(num) {
  if (num === 0) return '';
  if (num < 20) return units[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + units[num % 10] : '');
  if (num < 1000) return units[Math.floor(num / 100)] + ' hundred' + (num % 100 !== 0 ? ' ' + convertNumberToWordsIndian(num % 100) : '');
  if (num < 100000) return convertNumberToWordsIndian(Math.floor(num / 1000)) + ' thousand' + (num % 1000 !== 0 ? ' ' + convertNumberToWordsIndian(num % 1000) : '');
  if (num < 10000000) return convertNumberToWordsIndian(Math.floor(num / 100000)) + ' lakh' + (num % 100000 !== 0 ? ' ' + convertNumberToWordsIndian(num % 100000) : '');
  return convertNumberToWordsIndian(Math.floor(num / 10000000)) + ' crore' + (num % 10000000 !== 0 ? ' ' + convertNumberToWordsIndian(num % 10000000) : '');
}

const getAmountInWords = (totalAmount) => {
  let num = parseInt(totalAmount, 10);
  if (isNaN(num) || num === 0) {
    return 'ZERO';
  }
  return convertNumberToWordsIndian(num);
};

const ptToMm = (pt) => pt * 0.3528; // 1pt = 0.3528mm

const pageStyle = {
  width: '210mm',
  height: '297mm', // A4 height
  boxSizing: 'border-box',
  background: '#fff',
  fontFamily: 'Courier, monospace',
  color: '#000',
  position: 'relative',
  overflow: 'hidden', // Ensure watermark doesn't overflow
};

// Define font styles for consistency, directly mapping to PDFDocument calls
const fontStyles = {
  'Helvetica-Bold-8pt': { fontFamily: 'Helvetica', fontWeight: 'bold', fontSize: '8pt' },
  'Helvetica-8pt': { fontFamily: 'Helvetica', fontSize: '8pt' },
  'Courier-Bold-20pt': { fontFamily: 'Courier', fontWeight: 'bold', fontSize: '20pt' },
  'Courier-12pt': { fontFamily: 'Courier', fontSize: '12pt' },
  'Courier-Bold-15pt': { fontFamily: 'Courier', fontWeight: 'bold', fontSize: '15pt' },
  'Courier-15pt': { fontFamily: 'Courier', fontSize: '15pt' },
  'Courier-Bold-14pt': { fontFamily: 'Courier', fontWeight: 'bold', fontSize: '14pt' },
  'Courier-Bold-13pt': { fontFamily: 'Courier', fontWeight: 'bold', fontSize: '13pt' },
  'Courier-13pt': { fontFamily: 'Courier', fontSize: '13pt' },
  'Courier-Bold-12pt-list': { fontFamily: 'Courier', fontWeight: 'bold', fontSize: '12pt' }, // For table headers and bold text in signatures
  'Courier-12pt-text': { fontFamily: 'Courier', fontSize: '12pt' }, // For general 12pt Courier text
  'Courier-11pt': { fontFamily: 'Courier', fontSize: '11pt' },
  'Helvetica-Bold-13pt': { fontFamily: 'Helvetica', fontWeight: 'bold', fontSize: '13pt' },
  'Helvetica-12pt': { fontFamily: 'Helvetica', fontSize: '12pt' },
  'Courier-Bold-17pt': { fontFamily: 'Courier', fontWeight: 'bold', fontSize: '17pt' },
};


const BookedFlatInvoice = () => {
  const { bookedNo } = useParams();
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [watermarkDataUrl, setWatermarkDataUrl] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    // Function to create a data URL for the watermark image with specified opacity
    const createWatermarkImage = (src, opacity) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // Essential for loading images from different origins onto canvas
        img.src = src;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.globalAlpha = opacity; // Apply opacity here
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => {
          console.error("Failed to load watermark image for canvas processing.");
          resolve(null);
        };
      });
    };

    const fetchInvoice = async () => {
      try {
        const res = await api.getBookedFlatInvoiceDetails(bookedNo);
        if (res?.data?.success) {
          setInvoiceData(res.data.data);
          const logoSrc = Array.isArray(res.data.data.towerDetails?.inLogo)
            ? res.data.data.towerDetails.inLogo[0]
            : res.data.data.towerDetails?.inLogo;

          if (logoSrc) {
            setLogoDataUrl(logoSrc); // Store the original logo URL for the main logo
            const generatedWatermark = await createWatermarkImage(logoSrc, 0.2); // 0.2 opacity as per backend
            setWatermarkDataUrl(generatedWatermark);
          } else {
            setLogoDataUrl(null);
            setWatermarkDataUrl(null);
          }
        } else {
          setInvoiceData(null);
          setLogoDataUrl(null);
          setWatermarkDataUrl(null);
        }
      } catch (e) {
        console.error('Error fetching invoice data', e);
        setInvoiceData(null);
        setLogoDataUrl(null);
        setWatermarkDataUrl(null);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [bookedNo]);

  const handleDownload = () => {
    if (!printRef.current) return;
    const opt = {
      margin: 0, // html2pdf will handle margins based on pageStyle padding
      filename: `Agreement-${bookedNo}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, allowTaint: false }, // useCORS and allowTaint are crucial for images
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };
    html2pdf().set(opt).from(printRef.current).save();
  };

  // Wait for invoice data and watermark image processing to complete
  if (loading || (logoDataUrl && !watermarkDataUrl && logoDataUrl !== null)) {
    return (
      <>
        <Dashheader />
        <div style={{ padding: 20 }}>Loading invoice...</div>
      </>
    );
  }

  if (!invoiceData) {
    return (
      <>
        <Dashheader />
        <div style={{ padding: 20 }}>
          <button onClick={() => navigate(-1)}>Back</button>
          <h2>No data found for {bookedNo}</h2>
        </div>
      </>
    );
  }

  const { details, userDetails, towerDetails, schedules } = invoiceData;
  const displayLogoUrl = logoDataUrl; // Use the original logo URL for the main logo

  const bookedDate = details.bookedOn
    ? dayjs(details.bookedOn).format('DD-MM-YYYY')
    : '';

  const totalAmountInWords = getAmountInWords(details.totalAmount);
  const totalSchemeAmount = schedules?.scheme
    ?.reduce((sum, sch) => sum + (sch.amount || 0), 0)
    .toLocaleString('en-IN');

  const terms = [
    "* This agreement is legally enforceable.",
    "* By signing this agreement, the customer confirms that they have carefully read, understood and agreed to all terms and conditions mentioned herein.",
    "* All payments must be made only to the company's authorized representatives against official receipts.",
    "* The company shall not be responsible for any verbal promises or unofficial commitments made by any executive beyond what is mentioned in this agreement.",
    "* Minimum Booking Amount is USD 500,000 for all schemes.",
    "Payment Terms :",
    "* One-time payment must be completed within 45 days",
    "* For other schemes, payments must be made in EMIs.",
    "* The customer must strictly follow the payment scheme. In case of delay, the customer will be automatically shifted to the next higher scheme (e.g., from 'One-time payment' to '10-month' scheme), to ensure the project's timely completion.",
    "* An additional USD 250 per sq.ft. will be charged for every 10 months of delay in tenure.",
    "* Corpus Fund (non-refundable): 1.5 BHK - USD 50,000, 2 BHK - USD 75,000, 3BHK - USD 100,000.",
    "* All taxes, registration fees, and other miscellaneous charges, including 5% GST, will be borne by the customer.",
    "* If the buyer defaults on the payments, the builder reserves the right to cancel the deal and sell the unit to others.",
    "* If the buyer cancels the deal, USD 500,000 will be deducted from the refundable amount.",
    "* The remaining balance will be returned after obtaining the Occupancy Certificate (OC) from HMDA.",
    "* If the builder fails to hand over the project on time, the builder will pay monthly rent for the unit, calculated based on prevailing local market rental rates.",
    "* The project will be completed within 36 months from the RERA certificate issuance date: 17-06-2025.",
    "* Ground floor is reserved for commercial use, and all flat owners acknowledge this without objection.",
    "* The Convention Center is private property, owned by the owners of Jalsa Homes. This will be clearly mentioned in the Sale Deeds, and must be disclosed by the flat owner during any re-sale of the property.",
    "* Any legal disputes arising from this agreement shall fall exclusively under the jurisdiction of Hyderabad, where the Head Office of Jalsa Homes is located."
  ];

  // Renders the watermark image with absolute positioning and pre-applied opacity
  const renderWatermark = (url) => {
    if (!url) return null;
    return (
      <img
        src={url}
        alt="watermark"
        style={{
          position: 'absolute',
          left: `${ptToMm(100)}mm`, // 100pt from left as per backend
          top: `${ptToMm(150)}mm`, // 150pt from top as per backend
          width: `${ptToMm(400)}mm`, // 400pt width as per backend
          zIndex: 0, // Ensure it's behind other content
          objectFit: 'contain',
          pointerEvents: 'none', // Allow clicking through the watermark
        }}
      />
    );
  };

  // Renders the logo and project heading section
  const renderLogoSection = () => (
    <>
      <div style={{ position: 'absolute', left: `${ptToMm(20)}mm`, top: `${ptToMm(20)}mm` }}>
        {displayLogoUrl && (
          <img
            src={`${displayLogoUrl}?t=${Date.now()}`} // Cache-buster for fresh image load
            alt="logo"
            crossOrigin="anonymous" // Required for canvas operations and CORS
            style={{ width: `${ptToMm(90)}mm`, height: `${ptToMm(50)}mm`, objectFit: 'cover' }}
          />
        )}
      </div>
      <div style={{ ...fontStyles['Helvetica-Bold-8pt'], position: 'absolute', left: `${ptToMm(25)}mm`, top: `${ptToMm(77)}mm` }}>
        GARIKIPATI TOWERS
      </div>
      <div style={{ ...fontStyles['Helvetica-8pt'], position: 'absolute', left: `${ptToMm(30)}mm`, top: `${ptToMm(86)}mm` }}>
        by <span style={{ fontWeight: 'bold' }}>JALSA HOMES</span>
      </div>
    </>
  );


  return (
    <>
      <Dashheader />
        <div
        style={{
          background: '#e9e9e9', // A slightly darker background to make the page stand out
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column', // Align items in a column
          alignItems: 'center', // Center horizontally
          padding: '20px 0', // Add vertical padding, remove horizontal padding from here
        }}
      >
        <div // This div now acts as a container for buttons, centered above the page
          style={{
            marginBottom: '20px', // More space between buttons and page
            width: '210mm', // Match the width of the PDF page for alignment
            display: 'flex',
            justifyContent: 'flex-start', // Align buttons to the left within this container
            padding: '0 50px', // Add some padding similar to page margins if desired
            boxSizing: 'border-box', // Include padding in the width calculation
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              marginRight: '15px',
              padding: '8px 16px',
              backgroundColor: '#6c757d', // A neutral button color
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Back
          </button>
          <button
            onClick={handleDownload}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff', // A primary button color
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Download PDF
          </button>
        </div>

        <div ref={printRef}>
          {/* PAGE 1 */}
          <div style={pageStyle} className="page">
            {renderWatermark(watermarkDataUrl)}
            {renderLogoSection()}

                        <div
              style={{
                ...fontStyles['Courier-Bold-20pt'],
                position: 'absolute',
                left: `${ptToMm(200)}mm`, // Keep the 200pt left position from backend
                top: `${ptToMm(30)}mm`, // Keep the 30pt top position from backend
                whiteSpace: 'nowrap', // Prevent text from wrapping
              }}
            >
              AGREEMENT FOR SALE
            </div>


            {/* Date */}
            <div
              style={{
                ...fontStyles['Courier-12pt'],
                position: 'absolute',
                left: `${ptToMm(417)}mm`, // 417pt from left
                top: `${ptToMm(80)}mm`, // 80pt from top
              }}
            >
              Date : {bookedDate}
            </div>

            {/* Reference No */}
            <div
              style={{
                ...fontStyles['Courier-15pt'],
                position: 'absolute',
                left: `${ptToMm(50)}mm`,
                top: `${ptToMm(120)}mm`,
              }}
            >
              Reference No : <span style={fontStyles['Courier-15pt']}>{details.bookedNo || ''}</span>
            </div>

            {/* Customer photo box */}
            <div
              style={{
                position: 'absolute',
                left: `${ptToMm(450)}mm`, // 450pt left
                top: `${ptToMm(125)}mm`, // 125pt top
                width: `${ptToMm(100)}mm`,
                height: `${ptToMm(100)}mm`,
                border: '1px solid #000',
                textAlign: 'center',
                ...fontStyles['Courier-12pt'],
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div>Customer</div>
              <div>Photo</div>
            </div>

            {/* Details list */}
                        {/* Details list - Each item positioned absolutely for precision */}
                        {/* Details list - Each item positioned absolutely for precision */}
            <div style={{ position: 'relative', width: '100%', height: `${ptToMm(600)}mm` }}> {/* Increased height to accommodate flowing content */}
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(140)}mm` }}>
                Project Name : <span style={fontStyles['Courier-Bold-15pt']}>{details.towerName || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(162)}mm` }}>
                Customer Name : <span style={fontStyles['Courier-Bold-15pt']}>{userDetails?.name || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(184)}mm` }}>
                Id Proof :{' '}
                <span style={fontStyles['Courier-Bold-15pt']}>
                  {Array.isArray(userDetails?.govtIdProof)
                    ? userDetails.govtIdProof.join(', ')
                    : userDetails?.govtIdProof || details.govtIdProof || ''}
                </span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(206)}mm` }}>
                phone : <span style={fontStyles['Courier-Bold-15pt']}>{userDetails?.phoneNumber || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(228)}mm` }}>
                Unit type :{' '}
                <span style={fontStyles['Courier-Bold-15pt']}>
                  {details.unitType || ''} [ {details.bedrooms || ''} ]
                </span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(250)}mm` }}>
                Floor : <span style={fontStyles['Courier-Bold-15pt']}>{details.floor || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(272)}mm` }}>
                Flat No : <span style={fontStyles['Courier-Bold-15pt']}>{details.flatNo || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(294)}mm` }}>
                Block : <span style={fontStyles['Courier-Bold-15pt']}>{details.block || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(316)}mm` }}>
                Facing : <span style={fontStyles['Courier-Bold-15pt']}>{details.facing || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(338)}mm` }}>
                Premium Add-On : <span style={fontStyles['Courier-Bold-15pt']}>{details.specialFeature || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(360)}mm` }}>
                Parking Slot : <span style={fontStyles['Courier-Bold-15pt']}>{details.parkingSlot || 0}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(382)}mm` }}>
                Total sqft : <span style={fontStyles['Courier-Bold-15pt']}>{details.sqFeet || 0}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(404)}mm` }}>
                Actual per sqft Price : <span style={fontStyles['Courier-Bold-15pt']}>{details.actualSqPrice || 0}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(426)}mm` }}>
                Final per sqft Price : <span style={fontStyles['Courier-Bold-15pt']}>{details.finalSqPrice || ''}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(448)}mm` }}>
                Base Amount : <span style={fontStyles['Courier-Bold-15pt']}>{details.baseAmount || 0}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(470)}mm` }}>
                Premium Add-On Price : <span style={fontStyles['Courier-Bold-15pt']}>{details.specialFeaturePrice || 0}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(492)}mm` }}>
                Parking Charges : <span style={fontStyles['Courier-Bold-15pt']}>{details.parkingCharges || 0}</span>
              </div>
              <div style={{ ...fontStyles['Courier-15pt'], position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(514)}mm` }}>
                Total Amount :{' '}
                <span style={fontStyles['Courier-Bold-15pt']}>{details.totalAmount?.toLocaleString('en-IN') || 0}</span>
              </div>

              {/* New container for Amount in Words and Remarks to enable dynamic flow */}
              <div style={{ position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(536)}mm`, width: `${ptToMm(400)}mm` }}>
                <div style={{ ...fontStyles['Courier-15pt'], lineHeight: '1.5' }}> {/* Added lineHeight for consistent spacing */}
                  Amount in Words :{' '}
                  <span style={{ ...fontStyles['Courier-Bold-15pt'], display: 'inline' }}>{totalAmountInWords.toUpperCase()} ONLY</span>
                </div>
                {/* Remarks will now flow after Amount in Words. Calculate marginTop to mimic backend's moveDown() */}
                <div style={{ ...fontStyles['Courier-15pt'], marginTop: `${ptToMm(12)}mm` }}> {/* 39pt original difference - 15pt (one line height) = 24pt */}
                  Remarks : <span style={fontStyles['Courier-Bold-15pt']}>{details.remarks || ''}</span>
                </div>
              </div>
            </div>



            {/* Payment schedule */}
            <div
              style={{
                position: 'absolute',
                left: `${ptToMm(50)}mm`,
                right: `${ptToMm(50)}mm`,
                top: `${ptToMm(620)}mm`, // Approx Y position + moveDown + some margin
                textAlign: 'center',
              }}
            >
              <div style={fontStyles['Courier-Bold-14pt']}>
                Payment schedule
              </div>
            </div>

            <div
              style={{
                position: 'absolute',
                left: `${ptToMm(70)}mm`, // Col1 start
                right: `${ptToMm(50)}mm`,
                top: `${ptToMm(650)}mm`, // Approx Y for table top
              }}
            >
              <div style={{ display: 'flex', ...fontStyles['Courier-Bold-12pt-list'], marginBottom: `${ptToMm(5)}mm` }}>
                <div style={{ width: `${ptToMm(130)}mm` }}>Installment</div> {/* col1: 70, col2: 200 => diff 130 */}
                <div style={{ width: `${ptToMm(100)}mm` }}>Days</div> {/* col2: 200, col3: 300 => diff 100 */}
                <div style={{ width: `${ptToMm(150)}mm` }}>Due Date</div> {/* col3: 300, col4: 450 => diff 150 */}
                <div style={{ flex: 1, textAlign: 'right' }}>Amount</div>
              </div>

              {schedules?.scheme?.map((sch, idx) => (
                <div key={sch._id || idx} style={{ display: 'flex', ...fontStyles['Courier-12pt-text'], marginBottom: `${ptToMm(3)}mm` }}>
                  <div style={{ width: `${ptToMm(130)}mm` }}>{sch.percent}%</div>
                  <div style={{ width: `${ptToMm(100)}mm` }}>{sch.days}</div>
                  <div style={{ width: `${ptToMm(150)}mm` }}>
                    {sch.dueDate ? dayjs(sch.dueDate).format('DD-MM-YYYY') : ''}
                  </div>
                  <div style={{ flex: 1, textAlign: 'right' }}>
                    {sch.amount != null ? sch.amount.toLocaleString('en-IN') : ''}
                  </div>
                </div>
              ))}
            </div>

            {/* Total at bottom like PDF */}
            <div
              style={{
                position: 'absolute',
                left: `${ptToMm(377)}mm`, // Align with backend's 377pt left
                top: `${ptToMm(770)}mm`, // Exact Y position from backend
                ...fontStyles['Courier-Bold-13pt'],
              }}
            >
              Total :{' '}
              <span style={fontStyles['Courier-13pt']}>
                {totalSchemeAmount}
              </span>
            </div>
          </div>



          {/* PAGE 2 – Signatures */}
          <div style={pageStyle} className="page">
            {renderWatermark(watermarkDataUrl)}
            {renderLogoSection()}

            <div
              style={{
                ...fontStyles['Courier-Bold-20pt'],
                textAlign: 'center',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: `${ptToMm(40)}mm`, // Y position from backend
                width: `calc(100% - ${ptToMm(100)}mm)`,
              }}
            >
              AGREEMENT FOR SALE
            </div>

            <div style={{ position: 'absolute', left: `${ptToMm(50)}mm`, top: `${ptToMm(200)}mm`, ...fontStyles['Courier-13pt'] }}>
              <div style={{ marginBottom: `${ptToMm(20)}mm` }}> {/* doc.moveDown(2) */}
                Customer Signature : <span style={fontStyles['Courier-Bold-13pt']}>________________________</span>
              </div>
              <div style={{ marginBottom: `${ptToMm(20)}mm` }}> {/* doc.moveDown(2) */}
                Witness : <span style={fontStyles['Courier-Bold-13pt']}>1)____________________ 2) _______________</span>
              </div>
              <div style={{ marginBottom: `${ptToMm(20)}mm` }}> {/* doc.moveDown(2) */}
                Venders Signature : <span style={fontStyles['Courier-Bold-13pt']}>1) _____________________</span>
              </div>
              <div style={{ marginBottom: `${ptToMm(20)}mm`, marginLeft: `${ptToMm(280)}mm` }}> {/* Adjusted for 330pt left (50 + 280 = 330) */}
                <span style={fontStyles['Courier-Bold-13pt']}>2) _______________</span>
              </div>
              <div style={{ marginBottom: `${ptToMm(20)}mm`, marginLeft: `${ptToMm(280)}mm` }}> {/* Adjusted for 330pt left */}
                <span style={fontStyles['Courier-Bold-13pt']}>3) _______________</span>
              </div>
              <div style={{ position: 'absolute', left: `${ptToMm(20)}mm`, top: `${ptToMm(240)}mm` }}> {/* For JALSA HOMES */}
                <span style={fontStyles['Helvetica-12pt']}>For </span>
                <span style={fontStyles['Helvetica-Bold-13pt']}>JALSA HOMES</span>
              </div>
              <div style={{ position: 'absolute', left: `${ptToMm(10)}mm`, top: `${ptToMm(310)}mm` }}> {/* SIGNATURE WITH SEAL */}
                <span style={fontStyles['Courier-12pt-text']}>SIGNATURE WITH SEAL</span>
              </div>
            </div>
          </div>


          {/* PAGE 3 – Terms & Conditions */}
          <div style={pageStyle} className="page">
            {renderWatermark(watermarkDataUrl)}
            {renderLogoSection()}

                        <div
              style={{
                ...fontStyles['Courier-Bold-20pt'], // Changed to 20pt font size
                position: 'absolute',
                left: `${ptToMm(200)}mm`, // 200pt from left
                top: `${ptToMm(30)}mm`, // 30pt from top
                whiteSpace: 'nowrap', // Prevent text from wrapping
              }}
            >
              AGREEMENT FOR SALE
            </div>

            <div
              style={{
                ...fontStyles['Courier-Bold-13pt'],
                textAlign: 'center',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: `${ptToMm(50)}mm`, // Adjusted top position to be below main title
                width: `calc(100% - ${ptToMm(100)}mm)`,
              }}
            >
              TERMS & CONDITIONS
            </div>

            <div style={{ position: 'absolute', left: `${ptToMm(50)}mm`, right: `${ptToMm(50)}mm`, top: `${ptToMm(105)}mm`, ...fontStyles['Courier-11pt'], lineHeight: '1' }}>
              {terms.map((term, idx) => {
                if (term.startsWith('*')) {
                  return (
                    <div key={idx} style={{ textIndent: '-1em', paddingLeft: '1em', marginBottom: `${ptToMm(5)}mm`, width: `calc(100% - ${ptToMm(20)}mm)` }}>
                      &bull; {term.substring(1).trim()}
                    </div>
                  );
                } else {
                  return (
                    <div key={idx} style={{ ...fontStyles['Helvetica-Bold-13pt'], marginTop: `${ptToMm(0)}mm`, marginBottom: `${ptToMm(5)}mm`, width: '100%' }}>
                      {term.trim()}
                    </div>
                  );
                }
              })}
            </div>

            <div style={{ position: 'absolute', left: `${ptToMm(30)}mm`, bottom: `${ptToMm(50)}mm`, ...fontStyles['Courier-12pt-text'] }}> {/* Adjusted bottom position to give space */}
              <div style={{ marginBottom: `${ptToMm(10)}mm` }}>
                <b style={fontStyles['Courier-Bold-12pt-list']}>
                  Customer Signature :  ______________  ______________  ______________
                </b>
              </div>
              <div style={{ marginBottom: `${ptToMm(20)}mm`, marginLeft: `${ptToMm(200)}mm` }}> {/* Aligned with backend's 230pt offset */}
                <span style={{ display: 'inline-block', width: `${ptToMm(80)}mm` }}>1</span>
                <span style={{ display: 'inline-block', width: `${ptToMm(80)}mm` }}>2</span>
                <span>3</span>
              </div>
              <div style={{ marginBottom: `${ptToMm(10)}mm` }}>
                <b style={fontStyles['Courier-Bold-12pt-list']}>
                  Vender's Signature : ______________  ______________  ______________
                </b>
              </div>
              <div style={{ marginLeft: `${ptToMm(200)}mm` }}>
                <span style={{ display: 'inline-block', width: `${ptToMm(80)}mm` }}>1</span>
                <span style={{ display: 'inline-block', width: `${ptToMm(80)}mm` }}>2</span>
                <span>3</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookedFlatInvoice;
